import React, {useEffect, useRef, useState} from 'react';
import {useThemeProvider} from '../../../../mosaic/utils/ThemeContext.jsx';
import {chartColors} from '../../../../mosaic/charts/ChartjsConfig.jsx';
import {
    CategoryScale,
    Chart,
    Filler,
    LinearScale,
    LineController,
    LineElement,
    PointElement,
    TimeScale,
    Tooltip,
} from 'chart.js';
import 'chartjs-adapter-moment';
import {useTranslation} from "react-i18next";
import {useRenderDate, useRenderTime} from "../../../../api/utilities/useValueRenderers.js";


Chart.register(LineController, LineElement, Filler, PointElement, LinearScale, TimeScale, Tooltip, CategoryScale);

export default function LeadsByDateChart({data, width, height}) {

    let {t} = useTranslation();

    const [chart, setChart] = useState(null);
    const canvas = useRef(null);
    const {currentTheme} = useThemeProvider();
    const darkMode = currentTheme === 'dark';
    const {gridColor, tooltipBodyColor, tooltipBgColor, tooltipBorderColor} = chartColors;

    let renderDate = useRenderDate();
    let renderTime = useRenderTime();

    useEffect(() => {
        const ctx = canvas.current;

        const newChart = new Chart(ctx, {
            type: 'line',
            data: data,
            options: {
                layout: {
                    padding: {
                        top: 16,
                        bottom: 16,
                        left: 20,
                        right: 20,
                    },
                },
                scales: {
                    y: {
                        beginAtZero: true,
                        border: {
                            display: false,
                        },
                        grid: {
                            drawTicks: false,
                            color: darkMode ? gridColor.dark : gridColor.light,
                        },
                        ticks: {
                            maxTicksLimit: 2,
                            display: false,
                        },
                    },
                    x: {
                        display: false,
                    },
                },
                plugins: {
                    tooltip: {
                        callbacks: {
                            title: () => false, // Disable tooltip title
                            label: (context) => [
                                renderDate(new Date(Date.parse(context.label))),
                                renderTime(new Date(Date.parse(context.label)), true),
                                t('leads.x-leads', {count: context.raw})
                            ],
                        },
                        bodyColor: darkMode ? tooltipBodyColor.dark : tooltipBodyColor.light,
                        backgroundColor: darkMode ? 'rgba(15,25,40,0.5)' : 'rgba(255,255,255,0.5)',
                        borderColor: darkMode ? tooltipBorderColor.dark : tooltipBorderColor.light,
                        opacity: 0.5,
                    },
                    legend: {
                        display: false,
                    },
                },
                interaction: {
                    intersect: false,
                    mode: 'nearest',
                },
                maintainAspectRatio: false,
            },
        });
        setChart(newChart);
        return () => newChart.destroy();

    }, [data]);

    useEffect(() => {
        if (!chart) return;

        if (darkMode) {
            chart.options.scales.y.grid.color = gridColor.dark;
            chart.options.plugins.tooltip.bodyColor = tooltipBodyColor.dark;
            chart.options.plugins.tooltip.backgroundColor = tooltipBgColor.dark;
            chart.options.plugins.tooltip.borderColor = tooltipBorderColor.dark;
        } else {
            chart.options.scales.y.grid.color = gridColor.light;
            chart.options.plugins.tooltip.bodyColor = tooltipBodyColor.light;
            chart.options.plugins.tooltip.backgroundColor = tooltipBgColor.light;
            chart.options.plugins.tooltip.borderColor = tooltipBorderColor.light;
        }
        chart.update('none');

    }, [currentTheme]);

    return (
        <canvas ref={canvas} width={width} height={height}></canvas>
    );
}
